/* eslint-disable max-len */
/* eslint-disable react/no-array-index-key */
import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams, useHistory, useLocation, Switch } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import * as qs from 'query-string';
import { Dialog } from 'primereact/dialog';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { Button } from 'react-bootstrap';
import { AccountContext } from '../components/AccountContext';
import { RouteWithSubRoutes, routes } from './routes';
import { addLoggedAccount } from '../reducers/account';
import { addUserData } from '../reducers/app';
import { Loading } from '../components/Loading';
import { isAccountOfNewBusinessModelType } from '../utils/AppUtil';
import { CANCELLED, PayedBy } from '../shared/Constants';
import { growlErrorsWithMessage } from '../utils/NotificationUtil';
import { NotificationContext } from '../components/GrowlNotifications';
import { userHasOwnerRole } from '../services/apps/side-menu-builders/SideMenuHelper';

const VIEWER_QUERY = gql`
  query {
    viewer {
      id
      email
      firstName
      lastName
      country
      locale
    }
  }
`;

const i18n = defineMessages({
  congratulations: {
    id: 'account.subscription.congratulations',
    defaultMessage: 'Congratulations',
  },
  congratulationsAlmostDone: {
    id: 'account.subscription.congratulations-almost-done',
    defaultMessage: 'Congratulations - you are almost done!',
  },
  yourGpiAccountIsReady: {
    id: 'account.subscription.your-gpi-account-is-ready',
    defaultMessage: 'Your GR8PI account is ready!',
  },
  payedBySelfParagraph: {
    id: 'account.subscription.payed-by-self.paragraph',
    defaultMessage: 'Your organisation can start using it as soon as you have attached a NoLimit subscription to it.',
  },
  buyNow: {
    id: 'account.subscription.buy-now',
    defaultMessage: 'Buy now',
  },
  payedByParentParagraph: {
    id: 'account.subscription.payed-by-parent.paragraph',
    defaultMessage:
      'Your GPI representative will inform you as soon as the account is activated, and your organisation can start using it. Your GPI representative is:',
  },
  gscName: {
    id: 'account.subscription.gsc-name',
    defaultMessage: 'GSC name',
  },
  gscEmail: {
    id: 'account.subscription.gsc-email',
    defaultMessage: 'GSC e-mail',
  },
  paymentCancelled: {
    id: 'account.subscription.payment-cancelled',
    defaultMessage: 'Payment cancelled!',
  },
  pleaseTryAgain: {
    id: 'account.subscription.please-try-again',
    defaultMessage: 'Please try again.',
  },
});
/**
 * Mounted under `/accounts/:id` .
 * Will provide the account ID to all child components.
 * Read the documentation for `withAccount` function.
 */
const AccountRoutes = ({
  onAccountSelect,
  type,
  isHeadOfFamily,
  userRole,
  subscriptionInformation,
  gpiRepresentativeData,
}) => {
  const history = useHistory();
  const { aid: accountId } = useParams();
  const growl = useContext(NotificationContext);

  const location = useLocation();
  const { purchaseSubscription } = qs.parse(location.search);

  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const addAccount = (account) => dispatch(addLoggedAccount(account));

  const updateUserData = (data) => dispatch(addUserData(data));

  const { loading, error, data } = useQuery(VIEWER_QUERY);

  const dataExists = data && data.viewer;

  useEffect(() => {
    if (dataExists) {
      const { viewer } = data;

      updateUserData(viewer);

      if (accountId) {
        onAccountSelect(accountId);
        addAccount(accountId);
      }
    }
  });

  useEffect(() => {
    if (purchaseSubscription === CANCELLED) {
      const message = (
        <>
          <p>
            <strong>{formatMessage(i18n.paymentCancelled)}</strong>
          </p>
          <p>{formatMessage(i18n.pleaseTryAgain)}</p>
        </>
      );

      growlErrorsWithMessage(growl, message);
    }
    // eslint-disable-next-line
  }, [purchaseSubscription]);

  if (error) return `Error! ${error.message}`;

  if (loading) {
    return <Loading />;
  }

  const isNewBusinessModelAccount = isAccountOfNewBusinessModelType(type);

  const shouldBlockNavigation =
    isNewBusinessModelAccount &&
    isHeadOfFamily &&
    subscriptionInformation &&
    subscriptionInformation.payedBy &&
    !subscriptionInformation.hasActiveSubscription &&
    subscriptionInformation.hasNoPreviousSubscription;

  if (shouldBlockNavigation) {
    const userNeedsToBuySubscriptionForOwnAccount =
      subscriptionInformation.payedBy === PayedBy.PAYED_BY_SELF && !subscriptionInformation.hasActiveSubscription;
    const parentNeedsToPayForSubscription =
      subscriptionInformation.payedBy === PayedBy.PAYED_BY_PARENT && !subscriptionInformation.hasActiveSubscription;

    if (userNeedsToBuySubscriptionForOwnAccount) {
      const userIsOwner = userHasOwnerRole(userRole);
      const ownerEmail = subscriptionInformation.ownerOfPayingAccount;

      return (
        <Dialog visible closable={false} header={formatMessage(i18n.congratulationsAlmostDone)}>
          <div>
            {userIsOwner ? (
              <>
                <p>
                  <strong>{formatMessage(i18n.yourGpiAccountIsReady)}</strong>
                </p>
                <p>{formatMessage(i18n.payedBySelfParagraph)}</p>
                <br />
                <div className="align-center">
                  <Button
                    className="p-button buy-now"
                    onClick={() => history.push(`/buy-own-subscription?accountId=${accountId}`)}
                  >
                    {formatMessage(i18n.buyNow)}
                  </Button>
                </div>
              </>
            ) : (
              <FormattedMessage
                id="account.subscription.payed-by-self.contact-owner"
                defaultMessage="Your account is missing a subscription. Please contact {ownerEmail}."
                values={{ ownerEmail: <strong>{ownerEmail}</strong> }}
                tagName="p"
              />
            )}
          </div>
        </Dialog>
      );
    }

    if (parentNeedsToPayForSubscription) {
      const { parentAccountOwnerFullName, parentAccountOwnerEmail } = gpiRepresentativeData;

      return (
        <Dialog visible closable={false} header={formatMessage(i18n.congratulationsAlmostDone)}>
          <p>
            <strong>{formatMessage(i18n.yourGpiAccountIsReady)}</strong>
          </p>
          <p>{formatMessage(i18n.payedByParentParagraph)}</p>
          <p>{`${formatMessage(i18n.gscName)}: ${parentAccountOwnerFullName}`}</p>
          <p>{`${formatMessage(i18n.gscEmail)}: ${parentAccountOwnerEmail}`}</p>
        </Dialog>
      );
    }
  }

  return (
    <AccountContext.Provider value={accountId}>
      <Switch>
        {routes.map((route, i) => (
          <RouteWithSubRoutes key={`r-${i}`} {...route} />
        ))}
      </Switch>
    </AccountContext.Provider>
  );
};

AccountRoutes.propTypes = {
  onAccountSelect: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  isHeadOfFamily: PropTypes.bool.isRequired,
  userRole: PropTypes.string.isRequired,
  subscriptionInformation: PropTypes.objectOf(PropTypes.any).isRequired,
  gpiRepresentativeData: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default AccountRoutes;
